import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, HeartPulse, Wallet, FileDown, FileText, Sheet, Loader2, FileStack, Mail, ClipboardList, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/ui";
import { cn } from "@/lib/utils";

const TYPES = [
  { id: "compliance", label: "Compliance", icon: ShieldCheck, color: "#F79009" },
  { id: "clinical", label: "Clinical", icon: HeartPulse, color: "#0EA5E9" },
  { id: "financial", label: "Financial", icon: Wallet, color: "#12B76A" },
  { id: "operations", label: "Operations", icon: ClipboardList, color: "#7C3AED" },
];

const FORMATS = [
  { fmt: "pdf", label: "PDF", icon: FileText },
  { fmt: "xlsx", label: "Excel", icon: Sheet },
  { fmt: "csv", label: "CSV", icon: FileDown },
];

export default function Reports() {
  const { user } = useAuth();
  const [active, setActive] = useState("compliance");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [packLoading, setPackLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");

  const rangeQS = useCallback((type) => (
    type === "operations" && dfrom && dto ? `&from_date=${dfrom}&to_date=${dto}` : ""
  ), [dfrom, dto]);

  const load = useCallback(async (type) => {
    setLoading(true); setReport(null);
    try {
      const qs = type === "operations" && dfrom && dto ? `?from_date=${dfrom}&to_date=${dto}` : "";
      const { data } = await api.get(`/reports/${type}${qs}`);
      setReport(data);
      if (data.range && (!dfrom || !dto)) { setDfrom(data.range.from); setDto(data.range.to); }
    } catch (e) { toast.error("Could not load report"); }
    finally { setLoading(false); }
  }, [dfrom, dto]);

  useEffect(() => { load(active); }, [active, dfrom, dto, load]);

  const download = async (type, fmt) => {
    setExporting(fmt);
    try {
      const res = await api.get(`/reports/${type}/export?format=${fmt}${rangeQS(type)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `careiq-${type}-report.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} exported`);
    } catch (e) { toast.error("Export failed"); }
    finally { setExporting(null); }
  };

  const cfg = TYPES.find((t) => t.id === active);

  const emailPack = async () => {
    setEmailing(true);
    try { const { data } = await api.post("/email/board-pack"); toast.success(`Board Pack emailed to ${data.count} admin(s)`); }
    catch (e) { toast.error("Email failed"); }
    finally { setEmailing(false); }
  };

  const downloadPack = async () => {
    setPackLoading(true);
    try {
      const res = await api.get("/reports/board-pack/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "careiq-executive-board-pack.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Executive Board Pack exported");
    } catch (e) { toast.error("Board Pack export failed"); }
    finally { setPackLoading(false); }
  };

  return (
    <div>
      <PageHeader overline="Governance · Reports & Analytics" title="Reports & Analytics"
        description="Generate and export compliance, clinical, financial and operations reports for audits and leadership."
        actions={<div className="flex flex-wrap gap-2">
          {user?.role === "admin" && (
            <button data-testid="email-board-pack-btn" onClick={emailPack} disabled={emailing}
              className="flex items-center gap-2 rounded-md border border-[#7C3AED] px-3.5 py-2 text-sm font-semibold text-[#7C3AED] transition-colors hover:bg-[#7C3AED] hover:text-white disabled:opacity-60">
              {emailing ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Email to Admins
            </button>
          )}
          <button data-testid="board-pack-btn" onClick={downloadPack} disabled={packLoading}
            className="flex items-center gap-2 rounded-md bg-[#7C3AED] px-3.5 py-2 text-sm font-semibold text-white shadow-lg shadow-[#7C3AED]/25 transition-colors hover:bg-[#6D28D9] disabled:opacity-60">
            {packLoading ? <Loader2 size={16} className="animate-spin" /> : <FileStack size={16} />} Export Board Pack
          </button>
        </div>} />

      {/* Report type tabs */}
      <div className="mb-5 flex flex-wrap gap-2">
        {TYPES.map((t) => {
          const Icon = t.icon; const on = active === t.id;
          return (
            <button key={t.id} data-testid={`report-tab-${t.id}`} onClick={() => setActive(t.id)}
              className={cn("flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors",
                on ? "text-white shadow" : "bg-card text-muted-foreground hover:text-foreground")}
              style={on ? { backgroundColor: t.color, borderColor: t.color } : {}}>
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Date-range picker (dated reports only) */}
      {report?.dated && (
        <div data-testid="date-range-controls" className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border bg-card p-3 shadow-sm">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground"><CalendarRange size={16} className="text-[#7C3AED]" /> Reporting period</span>
          <input data-testid="range-from" type="date" value={dfrom} max={dto || undefined} onChange={(e) => setDfrom(e.target.value)}
            className="h-9 rounded-md border bg-background px-2.5 text-sm outline-none focus:border-[#7C3AED]" />
          <span className="text-muted-foreground">→</span>
          <input data-testid="range-to" type="date" value={dto} min={dfrom || undefined} onChange={(e) => setDto(e.target.value)}
            className="h-9 rounded-md border bg-background px-2.5 text-sm outline-none focus:border-[#7C3AED]" />
        </div>
      )}

      {/* Export bar */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card p-4 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <cfg.icon size={17} style={{ color: cfg.color }} />
          {report?.title || `${cfg.label} Report`}
          {report && <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{report.count} rows</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          {FORMATS.map((f) => {
            const Icon = f.icon;
            return (
              <button key={f.fmt} data-testid={`export-${active}-${f.fmt}`} disabled={!report || exporting}
                onClick={() => download(active, f.fmt)}
                className="flex items-center gap-1.5 rounded-md border bg-background px-3 py-2 text-xs font-semibold transition-colors hover:border-[#155EEF] hover:text-[#155EEF] disabled:opacity-50">
                {exporting === f.fmt ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />} {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading && <div className="flex h-40 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>}

      {report && !loading && (
        <>
          {/* KPIs */}
          <div className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {report.kpis.map((k) => (
              <div key={k.label} data-testid={`report-kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-xl border bg-card p-4 shadow-sm">
                <div className="text-2xl font-bold tracking-tight" style={{ color: k.critical ? "#F04438" : cfg.color }}>{k.value}</div>
                <div className="mt-1 overline">{k.label}</div>
              </div>
            ))}
          </div>

          {/* Table */}
          <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
            <div className="careiq-scroll max-h-[60vh] overflow-auto">
              <table className="w-full text-sm" data-testid="report-table">
                <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                  <tr>{report.columns.map((c) => <th key={c} className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-muted-foreground">{c}</th>)}</tr>
                </thead>
                <tbody className="divide-y">
                  {report.rows.map((r, i) => (
                    <tr key={i} className="transition-colors hover:bg-muted/40">
                      {report.keys.map((k) => <td key={k} className="whitespace-nowrap px-4 py-2.5">{fmtCell(r[k])}</td>)}
                    </tr>
                  ))}
                  {report.rows.length === 0 && <tr><td colSpan={report.columns.length} className="px-4 py-8 text-center text-muted-foreground">No data</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function fmtCell(v) {
  if (v == null) return "—";
  if (typeof v === "number") return v.toLocaleString();
  return String(v);
}
